import { useState, type KeyboardEvent } from "react";
import AppShell from "../../components/app/AppShell";
import { Eyebrow } from "../../components/design";
import { STUB_BOOK } from "../../lib/stubs";

// /app/book/share — Recipient chips, subject, Caveat-style begleittext.
// Reference: extras.jsx PageShareBook.

const DEFAULT_LETTER = `Liebe Familie,

Omas Buch ist fertig — drei Monate Telefonate, sieben Kapitel, ein Leben. Lest es nicht auf einmal. Vielleicht das Kapitel mit Helmut, wenn ihr Zeit habt, ein Glas Wein und eine ruhige Stunde.

In Liebe,
Marie`;

function isEmail(s: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function AppBookShare() {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [subject, setSubject] = useState("Hildegards Geschichte für Dich");
  const [letter, setLetter] = useState(DEFAULT_LETTER);
  const [sent, setSent] = useState(false);

  function commitDraft() {
    const parts = draft
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const next = [...recipients];
    for (const p of parts) {
      if (isEmail(p) && !next.includes(p)) next.push(p);
    }
    setRecipients(next);
    setDraft("");
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitDraft();
    } else if (e.key === "Backspace" && !draft && recipients.length) {
      setRecipients(recipients.slice(0, -1));
    }
  }

  function removeChip(email: string) {
    setRecipients(recipients.filter((r) => r !== email));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // TODO(Phase C): generate signed 30-day URL + send transactional email.
    setSent(true);
  }

  return (
    <AppShell
      eyebrow="PDF teilen · Begleittext"
      title={
        <>
          PDF <em>teilen</em> · Begleittext.
        </>
      }
      backTo="/app/book"
    >
      <form onSubmit={onSubmit} style={{ padding: "0 22px 90px" }}>
        {sent && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: "rgba(95,107,79,0.12)",
              border: "1px solid rgba(95,107,79,0.30)",
              borderLeft: "3px solid var(--sage)",
              borderRadius: 4,
              padding: "10px 14px",
              color: "var(--sage)",
              fontFamily: "var(--f-body)",
              fontSize: 14,
              marginBottom: 18,
            }}
          >
            E-Mail versendet — der Link ist 30 Tage gültig.
          </div>
        )}

        {/* Recipients */}
        <Eyebrow>An</Eyebrow>
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.14)",
            borderRadius: 10,
            padding: "10px 12px",
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 18,
            alignItems: "center",
          }}
        >
          {recipients.map((r) => (
            <span
              key={r}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 6px 5px 10px",
                borderRadius: 999,
                background: "var(--oxblood)",
                color: "var(--paper-50)",
                fontFamily: "var(--f-ui)",
                fontSize: 12,
              }}
            >
              {r}
              <button
                type="button"
                aria-label={`${r} entfernen`}
                onClick={() => removeChip(r)}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 2,
                  display: "inline-flex",
                  cursor: "pointer",
                  color: "var(--paper-50)",
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          <input
            type="email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onBlur={commitDraft}
            placeholder={recipients.length ? "" : "name@familie.de · Enter"}
            aria-label="E-Mail-Adresse hinzufügen"
            style={{
              flex: 1,
              minWidth: 140,
              border: "none",
              background: "transparent",
              outline: "none",
              fontFamily: "var(--f-body)",
              fontSize: 14,
              color: "var(--ink)",
            }}
          />
        </div>

        {/* Subject */}
        <Eyebrow>Betreff</Eyebrow>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          aria-label="Betreff"
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.14)",
            borderRadius: 10,
            padding: "14px",
            marginBottom: 18,
            fontFamily: "var(--f-display)",
            fontSize: 16,
            color: "var(--ink)",
          }}
        />

        {/* Begleittext */}
        <Eyebrow>Begleittext</Eyebrow>
        <textarea
          rows={6}
          value={letter}
          onChange={(e) => setLetter(e.target.value)}
          aria-label="Begleittext"
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.14)",
            borderRadius: 10,
            padding: "16px",
            marginBottom: 18,
            fontFamily: "var(--f-script)",
            fontSize: 22,
            lineHeight: 1.4,
            color: "var(--ink)",
            transform: "rotate(-0.3deg)",
            resize: "vertical",
            minHeight: 160,
          }}
        />

        {/* Attachment row */}
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 12,
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <div
            aria-hidden
            style={{
              width: 38,
              height: 46,
              borderRadius: 4,
              background: "var(--paper-200)",
              border: "1px solid rgba(43,26,15,0.14)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontFamily: "var(--f-ui)",
              fontSize: 9,
              fontWeight: 700,
              color: "var(--oxblood)",
              letterSpacing: "0.12em",
            }}
          >
            PDF
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: "var(--f-display)",
                fontWeight: 500,
                fontSize: 14,
                color: "var(--ink)",
              }}
            >
              {STUB_BOOK.filename}
            </div>
            <div
              style={{
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 11.5,
                color: "var(--ink-faint)",
              }}
            >
              {STUB_BOOK.pages} Seiten · {STUB_BOOK.size}
            </div>
          </div>
        </div>

        {/* Privacy */}
        <div
          style={{
            background: "rgba(168,122,58,0.10)",
            borderLeft: "3px solid var(--ochre)",
            padding: "10px 14px",
            borderRadius: 4,
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 12.5,
            lineHeight: 1.4,
            color: "var(--ink-soft)",
            marginBottom: 18,
          }}
        >
          Der Link funktioniert nur für diese E-Mail-Adressen und läuft in 30
          Tagen ab.
        </div>

        <button
          type="submit"
          className="btn-primary"
          disabled={recipients.length === 0}
          style={{ width: "100%", boxSizing: "border-box" }}
        >
          Per E-Mail senden
        </button>
      </form>
    </AppShell>
  );
}
